import Vue from 'vue'
import axios from 'axios'

let nextId = 1
function newId () {
  return `menu-${nextId++}`
}

function syncNextId (items) {
  const scan = (list) => {
    for (const item of list) {
      if (typeof item.id === 'string') {
        const match = item.id.match(/^menu-(\d+)$/)
        if (match) {
          const n = parseInt(match[1], 10)
          if (n >= nextId) nextId = n + 1
        }
      }
      if (Array.isArray(item.children)) scan(item.children)
    }
  }
  scan(items)
}

const DEFAULT_ITEMS = []

function cloneDefaults () {
  return {
    menuItems: JSON.parse(JSON.stringify(DEFAULT_ITEMS)),
    savedItems: JSON.parse(JSON.stringify(DEFAULT_ITEMS))
  }
}

const state = {
  menuItems: cloneDefaults().menuItems,
  savedItems: cloneDefaults().savedItems,
  expandedIds: [],
  unsavedCount: 0
}

const getters = {
  menuItems: (s) => s.menuItems,
  savedItems: (s) => s.savedItems,
  expandedIds: (s) => s.expandedIds,
  hasUnsavedChanges: (s) => s.unsavedCount > 0,
  unsavedCount: (s) => s.unsavedCount
}

const mutations = {
  setMenuItems (state, items) {
    state.menuItems = items
  },
  setSavedItems (state, items) {
    state.savedItems = items
  },
  setExpandedIds (state, ids) {
    state.expandedIds = ids
  },
  toggleExpand (state, id) {
    const i = state.expandedIds.indexOf(id)
    if (i >= 0) {
      state.expandedIds = state.expandedIds.filter((x) => x !== id)
    } else {
      state.expandedIds = [...state.expandedIds, id]
    }
  },
  addMenuItem (state, item) {
    state.menuItems.push(item)
  },
  updateMenuItem (state, { item, updates }) {
    Object.assign(item, updates)
  },
  removeMenuItem (state, { parent, childIndex, topLevelIndex }) {
    if (parent !== undefined && parent !== null) {
      if (Array.isArray(parent.children)) {
        parent.children.splice(childIndex, 1)
      }
    } else if (topLevelIndex != null) {
      state.menuItems.splice(topLevelIndex, 1)
    }
    state.unsavedCount += 1
  },
  // Reorder within a single list. `parent` is the owning item, null for the top level.
  moveMenuItem (state, { parent, fromIndex, toIndex }) {
    const list = parent ? parent.children : state.menuItems
    if (!Array.isArray(list)) return
    if (fromIndex == null || toIndex == null || fromIndex === toIndex) return
    if (fromIndex < 0 || fromIndex >= list.length) return
    const [moved] = list.splice(fromIndex, 1)
    const insertIndex = fromIndex < toIndex ? toIndex - 1 : toIndex
    list.splice(insertIndex, 0, moved)
    state.unsavedCount += 1
  },
  addSubItem (state, { parent, child }) {
    if (!Array.isArray(parent.children)) {
      Vue.set(parent, 'children', [])
    }
    parent.children.push(child)
    if (!state.expandedIds.includes(parent.id)) {
      state.expandedIds = [...state.expandedIds, parent.id]
    }
  },
  discardChanges (state) {
    state.menuItems = JSON.parse(JSON.stringify(state.savedItems))
    state.unsavedCount = 0
  },
  publishMenu (state) {
    state.savedItems = JSON.parse(JSON.stringify(state.menuItems))
    state.unsavedCount = 0
  },
  setMenuFromApi (state, items) {
    if (items && Array.isArray(items) && items.length > 0) {
      state.menuItems = JSON.parse(JSON.stringify(items))
      state.savedItems = JSON.parse(JSON.stringify(items))
      syncNextId(items)
    }
  }
}

const actions = {
  newId () {
    return newId()
  },
  async fetchMenu ({ commit }) {
    try {
      const { data } = await axios.get('/api/menu')
      const items = data && data.data
      if (items && Array.isArray(items) && items.length > 0) {
        commit('setMenuFromApi', items)
      }
    } catch (_) {
      // Keep current (default) items when API fails or returns empty
    }
  },
  async saveMenu ({ state, commit }) {
    try {
      const payload = { items: JSON.parse(JSON.stringify(state.menuItems)) }
      const { data } = await axios.put('/api/menu', payload)
      commit('publishMenu')
      if (data && Array.isArray(data.data) && data.data.length > 0) {
        commit('setMenuFromApi', data.data)
      }
    } catch (_) {
      throw new Error('Failed to save menu')
    }
  }
}

export default {
  state,
  getters,
  mutations,
  actions,
  namespaced: true
}
